/**
 * Deler meldingstekst i tekst- og lenkesegmenter.
 *
 * Meldingene ble tidligere rendret som `<div>{msg.content}</div>`. Preact
 * escaper tekst, så en sporings-URL fra ordreoppslaget kom ut som død tekst
 * kunden måtte markere og kopiere for hånd.
 *
 * Vi returnerer segmenter i stedet for HTML med vilje: innholdet kommer fra
 * en språkmodell (og fra operatører), og skal ALDRI innom
 * `dangerouslySetInnerHTML`. Renderen bygger ekte <a>-elementer av
 * segmentene, så det finnes ikke noe injeksjonspunkt.
 */
export type Segment =
  | { type: "text"; value: string }
  | { type: "link"; href: string; label: string; internal: boolean };

// URL med protokoll, bar www-adresse, eller e-postadresse.
const TOKEN =
  /(https?:\/\/[^\s<>]+|www\.[^\s<>]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

const TRAILING_PUNCT = /[.,;:!?'"»)\]]/;

function count(text: string, ch: string): number {
  let n = 0;
  for (const c of text) if (c === ch) n++;
  return n;
}

/**
 * Skiller lenken fra tegnsetting som tilhører setningen.
 * "spor den her: https://x.no/a." → lenken er ".../a", punktumet er tekst.
 * Balanserte parenteser i selve URL-en beholdes (Wikipedia-stil "..._(bar)").
 */
function splitTrailing(raw: string): { core: string; tail: string } {
  let core = raw;
  let tail = "";
  while (core.length > 0) {
    const ch = core[core.length - 1];
    if (!TRAILING_PUNCT.test(ch)) break;
    // Kun en ubalansert slutt-parentes hører til setningen, ikke lenken.
    if (ch === ")" && count(core, ")") <= count(core, "(")) break;
    if (ch === "]" && count(core, "]") <= count(core, "[")) break;
    core = core.slice(0, -1);
    tail = ch + tail;
  }
  return { core, tail };
}

function toHref(core: string): string | null {
  let href: string;
  if (/^https?:\/\//i.test(core)) href = core;
  else if (/^www\./i.test(core)) href = "https://" + core;
  else if (core.includes("@")) href = "mailto:" + core;
  else return null;

  // Siste skanse: kun disse tre protokollene slipper ut i en href.
  // Stopper bl.a. javascript:/data: hvis mønsteret over noen gang utvides.
  if (!/^(https?:\/\/|mailto:)/i.test(href)) return null;
  return href;
}

const POLICY_LABELS: Record<string, string> = {
  "terms-of-service": "Handelsvilkår",
  "refund-policy": "Angrerett",
  "shipping-policy": "Fraktvilkår",
  "privacy-policy": "Personvern",
};

function hostOf(href: string): string {
  try {
    return new URL(href).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** Egen butikk → samme fane. Alt annet (sporing, e-post) → ny fane. */
export function isInternal(href: string): boolean {
  return hostOf(href) === "badstugutta.no";
}

/**
 * Lesbar lenketekst i stedet for en rå 120-tegns URL.
 * badstugutta.no/collections/vedfyrte-badstuovner → «Vedfyrte badstuovner».
 * Andre domener → «fedex.com/fedextrack…» (vertsnavn + kort sti).
 */
export function prettyLabel(href: string): string {
  if (href.startsWith("mailto:")) return href.slice(7);
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const host = url.hostname.replace(/^www\./, "");
  const parts = url.pathname.split("/").filter(Boolean);
  if (host !== "badstugutta.no") {
    const rest = url.pathname + url.search;
    return host + (rest.length > 1 ? (rest.length > 18 ? rest.slice(0, 18) + "…" : rest) : "");
  }
  if (parts.length === 0) return "badstugutta.no";
  let slug = parts[parts.length - 1];
  if (parts[0] === "policies" && POLICY_LABELS[slug]) return POLICY_LABELS[slug];
  try {
    slug = decodeURIComponent(slug);
  } catch {
    /* behold rå slug */
  }
  // Shopify-duplikatsuffiks («materialer-1») og sifre limt på et ord («elektrisk0»).
  const words = slug.replace(/-1$/, "").split("-").map((w) => w.replace(/^([a-zæøå]+)\d$/i, "$1"));
  const label = words.join(" ").trim() || host;
  const cap = label.charAt(0).toUpperCase() + label.slice(1);
  return cap.length > 60 ? cap.slice(0, 57) + "…" : cap;
}

export function linkify(text: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;

  // Regexen er global og deles mellom kall — nullstill før bruk.
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(text)) !== null) {
    const raw = match[0];
    const { core, tail } = splitTrailing(raw);
    const href = toHref(core);

    if (!href || core.length === 0) continue;

    if (match.index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: "link", href, label: prettyLabel(href), internal: isInternal(href) });
    if (tail) segments.push({ type: "text", value: tail });
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
}
